import * as React from 'react';
import { cn } from '@/lib/utils';
import { useFocusTrap } from '@/lib/use-focus-trap';

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  // DOMRect of the trigger that opened this popover, or null when
  // there's no positional anchor (renders centered).
  anchorRect: DOMRect | null;
  // Preferred placement relative to the anchor. The popover flips
  // vertically if it would overflow the viewport.
  placement?: 'below' | 'above';
  className?: string;
  children: React.ReactNode;
}

// Lightweight popover primitive — no portal, no Radix dep.
//
// - Anchored to a passed-in DOMRect, positioned `fixed`.
// - Closes on Escape, click outside, scroll, or resize.
// - Returns focus to the previously focused element on close.
// - Caller is responsible for managing the `open` state and passing
//   the trigger's DOMRect (typically captured in the trigger's
//   onClick via event.currentTarget.getBoundingClientRect()).
export function Popover({
  open,
  onClose,
  anchorRect,
  placement = 'below',
  className,
  children,
}: PopoverProps) {
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);

  // Keep keyboard focus inside the picker while open. (It's a non-modal
  // dialog — outside clicks pass through and close it — so we do NOT
  // claim aria-modal, but we do trap Tab so the keyboard path matches.)
  useFocusTrap(cardRef, open);

  React.useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    function onDocPointer(e: PointerEvent) {
      const target = e.target as Node | null;
      if (cardRef.current && target && cardRef.current.contains(target)) return;
      onClose();
    }
    function onScrollOrResize(e?: Event) {
      // Page/ancestor scroll detaches the popover from its anchor —
      // close. But ignore scrolls happening INSIDE the popover card
      // (the picker body has its own overflow-y-auto).
      if (e && e.type === 'scroll') {
        const target = e.target as Node | null;
        if (target && cardRef.current?.contains(target)) return;
      }
      onClose();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDocPointer, true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    const first = cardRef.current?.querySelector<HTMLElement>(
      'input, button, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDocPointer, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      restoreFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const style = computePosition(anchorRect, placement);

  return (
    <>
      <div
        aria-hidden
        className="fixed inset-0 z-40 bg-foreground/5"
        // backdrop is purely visual — clicks pass through via the
        // document pointerdown listener above
        style={{ pointerEvents: 'none' }}
      />
      <div
        ref={cardRef}
        role="dialog"
        className={cn(
          'fixed z-50 origin-top rounded-lg border border-border bg-popover text-popover-foreground shadow-lg',
          'animate-[panel-enter_120ms_ease-out]',
          className,
        )}
        style={style}
      >
        {children}
      </div>
    </>
  );
}

function computePosition(
  rect: DOMRect | null,
  placement: 'below' | 'above',
): React.CSSProperties {
  if (!rect) {
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }
  const margin = 8;
  const cardWidth = 380;
  const cardMaxHeight = 420;

  let top: number;
  if (placement === 'below') {
    const wouldOverflowBelow = rect.bottom + margin + cardMaxHeight > window.innerHeight;
    top = wouldOverflowBelow ? rect.top - margin - cardMaxHeight : rect.bottom + margin;
  } else {
    const wouldOverflowAbove = rect.top - margin - cardMaxHeight < 0;
    top = wouldOverflowAbove ? rect.bottom + margin : rect.top - margin - cardMaxHeight;
  }

  // Horizontally center on the anchor, clamped to viewport.
  const anchorCenter = rect.left + rect.width / 2;
  let left = anchorCenter - cardWidth / 2;
  left = Math.max(margin, Math.min(window.innerWidth - cardWidth - margin, left));

  return {
    top: Math.max(margin, top),
    left,
    width: cardWidth,
  };
}
